import { Box } from "@mui/material"
import { Clock, Network, RefreshCw, Server, Users } from "lucide-react"

import { StatsCard, StatsCardDual } from "@crm-src/components/stats-card"
import type { CustomerStats } from "@crm-src/types/customer"

export interface CustomerStatsCardsProps {
  /** Headcount, read off the list pagination meta (not the stats payload). */
  totalCount: number
  /** `/customers/stats`; null until the first fetch resolves. */
  stats: CustomerStats | null
}

/**
 * The five stat cards above the Customers list.
 *
 * Order is deliberate: headcount, then the installed footprint it
 * explains (servers, then ports), then the two renewal clocks.
 *
 * The footprint numbers are summed by crm-api's
 * `#calculate_customer_stats` over the WHOLE scoped customer base —
 * never over the 25 rows currently on screen — and are non-monetary, so
 * they render for a viewer without `crm:financials`.
 *
 * They replaced the previous "VIP Customers" / "At Risk" cards, which
 * read `vip_customers` / `at_risk_customers` — counters the stats
 * endpoint has never actually shipped, so both always rendered a
 * hard "0" that looked like real (broken) data.
 */
export function CustomerStatsCards({ totalCount, stats }: CustomerStatsCardsProps) {
  return (
    <Box data-testid="customer-stats" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2 }}>
      <StatsCard
        title="Total Customers"
        value={totalCount}
        icon={Users}
        iconColor="text-blue-600"
      />
      <StatsCard
        title="Servers"
        value={stats?.total_servers ?? 0}
        valueColor="text-emerald-600"
        icon={Server}
        iconColor="text-emerald-600"
      />
      <StatsCardDual
        title="Ports"
        items={[
          { label: "audio", value: stats?.total_audio_ports ?? 0, valueColor: "text-indigo-500" },
          { label: "web", value: stats?.total_web_ports ?? 0, valueColor: "text-blue-600" },
        ]}
        icon={Network}
        iconColor="text-indigo-500"
      />
      <StatsCard
        title="Renewals Close to Due"
        value={stats?.contracts_expiring_soon || 0}
        valueColor="text-yellow-600"
        icon={Clock}
        iconColor="text-yellow-600"
      />
      <StatsCard
        title="Renewals Due"
        value={stats?.renewals_due_soon || 0}
        valueColor="text-red-600"
        icon={RefreshCw}
        iconColor="text-red-600"
      />
    </Box>
  )
}
